import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "../axios-orders";
import type {
  TagResponse,
  CreateTagRequest,
  UpdateTagRequest,
  TagPaginatedResponse,
} from "../types/tag.type";

// Query Keys
//
// `paginated` now carries `query` as a fourth key segment (Task 4) — every mutation below
// invalidates via `tagKeys.all` (`["tags"]`), which still prefix-matches
// `["tags", "paginated", page, size, query]` regardless of what `query` is, exactly the way it
// already prefix-matched the three-segment key. Nothing about create/update/delete invalidation
// needed to change for this to keep working — verified by reading TanStack Query's own prefix-
// match semantics (a query key matches an invalidation filter when the filter key is a prefix of
// it), not assumed.
export const tagKeys = {
  all: ["tags"] as const,
  lists: () => [...tagKeys.all, "list"] as const,
  list: () => [...tagKeys.lists()] as const,
  paginated: (page: number, size: number, query: string) =>
    [...tagKeys.all, "paginated", page, size, query] as const,
  inUse: () => [...tagKeys.all, "in-use"] as const,
  detail: (id: string) => [...tagKeys.all, "detail", id] as const,
};

// ============================================
// TAG CRUD OPERATIONS
// ============================================

/**
 * Get all tags (simple list for dropdowns)
 * GET /tags
 */
export const useGetTags = () => {
  return useQuery<TagResponse[]>({
    queryKey: tagKeys.list(),
    queryFn: async (): Promise<TagResponse[]> => {
      const response = await axios.get<TagResponse[]>("/tags");
      return response.data;
    },
  });
};

/**
 * Get only the tags actually assigned to at least one material — for the material-list tag FILTER,
 * which must not offer tags nobody has used. Tag management still uses `useGetTags` (every tag).
 * GET /tags/in-use
 */
export const useGetTagsInUse = () => {
  return useQuery<TagResponse[]>({
    queryKey: tagKeys.inUse(),
    queryFn: async (): Promise<TagResponse[]> => {
      const response = await axios.get<TagResponse[]>("/tags/in-use");
      return response.data;
    },
  });
};

/**
 * Get paginated tags, optionally narrowed by a server-side search term.
 * GET /tags/paginated?page=0&size=20&query=...
 *
 * `query` mirrors `usePaginatedSitesByCompany`/`usePaginatedUsersByCompany` (hooks/useSites.ts,
 * hooks/useUsers.ts) field-for-field: appended to the request only when non-empty (an empty
 * string on the wire would defeat `TagRepository#searchByCompany`'s own blank-query fast path —
 * see that repository's comment — for no benefit), and `placeholderData: (previous) => previous`
 * so paging/searching doesn't flash empty between requests, the same TanStack Query option both
 * sibling hooks use.
 */
export const useGetTagsPaginated = (
  page: number = 0,
  size: number = 20,
  query: string = ""
) => {
  return useQuery<TagPaginatedResponse>({
    queryKey: tagKeys.paginated(page, size, query),
    queryFn: async (): Promise<TagPaginatedResponse> => {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
      });
      if (query) params.append("query", query);
      const response = await axios.get<TagPaginatedResponse>(
        `/tags/paginated?${params.toString()}`
      );
      return response.data;
    },
    placeholderData: (previous) => previous,
  });
};

/**
 * Get tag by ID
 * GET /tags/{id}
 */
export const useGetTagById = (id: string, enabled: boolean = true) => {
  return useQuery<TagResponse>({
    queryKey: tagKeys.detail(id),
    queryFn: async (): Promise<TagResponse> => {
      const response = await axios.get<TagResponse>(`/tags/${id}`);
      return response.data;
    },
    enabled: enabled && !!id,
  });
};

/**
 * Create a new tag
 * POST /tags
 */
export const useCreateTag = (
  onSuccess?: (tag: TagResponse) => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<TagResponse, unknown, CreateTagRequest>({
    mutationFn: async (data: CreateTagRequest): Promise<TagResponse> => {
      const response = await axios.post<TagResponse>("/tags", data);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate tag queries to refetch
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      onSuccess?.(data);
    },
    onError,
  });
};

/**
 * Update a tag
 * PUT /tags/{id}
 */
export const useUpdateTag = (
  onSuccess?: (tag: TagResponse) => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<
    TagResponse,
    unknown,
    { id: string; data: UpdateTagRequest }
  >({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateTagRequest;
    }): Promise<TagResponse> => {
      const response = await axios.put<TagResponse>(`/tags/${id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate all tag queries and the specific tag
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.detail(data.id) });
      onSuccess?.(data);
    },
    onError,
  });
};

/**
 * Delete a tag (soft delete)
 * DELETE /tags/{id}
 */
export const useDeleteTag = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, string>({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`/tags/${id}`);
    },
    onSuccess: () => {
      // Invalidate tag queries to refetch
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
      onSuccess?.();
    },
    onError,
  });
};

// ============================================
// BULK TAG ASSIGNMENT
// ============================================

export interface BulkTagAssignmentRequest {
  materialIds: string[];
  tagIds: string[];
}

/**
 * Assign tags to multiple materials in bulk
 * POST /material/bulk/tags
 */
export const useBulkAssignTags = (
  onSuccess?: () => void,
  onError?: (error: unknown) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, BulkTagAssignmentRequest>({
    mutationFn: async (data: BulkTagAssignmentRequest): Promise<void> => {
      await axios.post("/material/bulk/tags", data);
    },
    onSuccess: () => {
      // Invalidate material queries to refetch updated materials
      queryClient.invalidateQueries({ queryKey: ["ALL_COMPANY_MATERIALS"] });
      queryClient.invalidateQueries({ queryKey: ["MATERIALS"] });
      queryClient.invalidateQueries({ queryKey: ["ALL_MATERIALS"] });
      queryClient.invalidateQueries({ queryKey: ["FOUND_MATERIALS"] });
      onSuccess?.();
    },
    onError,
  });
};
